// src/components/notifications/NotificationCenter.js
import React, { useEffect, useState } from 'react';
import {
    Box,
    Badge,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Button,
    Tooltip,
    Snackbar,
    Alert,
    AlertTitle
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Support as SupportIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    MarkEmailRead as MarkReadIcon,
    NotificationsActive as NotificationsActiveIcon
} from '@mui/icons-material';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useSocket } from '../../hooks/useSocket';
import { useNavigate } from 'react-router-dom';

// Componente para el centro de notificaciones
const NotificationCenter = () => {
    const navigate = useNavigate();
    const { client, clientToken } = useClientAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [alert, setAlert] = useState(null);

    // Obtener la conexión socket y su estado
    const { socket, isConnected, error } = useSocket();

    // Cargar notificaciones iniciales
    useEffect(() => {
        if (client && clientToken) {
            fetchNotifications();
        }
    }, [client, clientToken]);

    // Configurar socket para notificaciones en tiempo real
    useEffect(() => {
        // Verificar que socket y client existan y que socket sea válido
        if (!socket || !client || !isConnected) return;

        console.log('NotificationCenter: Socket conectado, configurando eventos...');

        try {
            // Registrar este cliente con el socket
            socket.emit('register_client', client.id);
            socket.emit('join_notification_room', client.id);

            // Escuchar nuevas notificaciones
            socket.on('new_notification', ({ notification, highPriority }) => {
                console.log('Nueva notificación recibida:', notification);

                // Añadir a la lista local
                setNotifications(prev => [notification, ...prev]);

                // Incrementar contador de no leídas
                setUnreadCount(prev => prev + 1);

                // Mostrar alerta si es de alta prioridad
                if (highPriority) {
                    setAlert({
                        severity: getNotificationSeverity(notification.type),
                        title: notification.title,
                        message: notification.message,
                        type: notification.type
                    });
                }
            });

            // Recibir notificaciones no leídas al conectar
            socket.on('unread_notifications', ({ count, notifications }) => {
                console.log(`Recibidas ${count} notificaciones no leídas`);
                setUnreadCount(count);
                setNotifications(notifications);
            });

            // Recibir alertas importantes
            socket.on('important_alert', (alertData) => {
                setAlert({
                    severity: getNotificationSeverity(alertData.type),
                    title: alertData.title,
                    message: alertData.message,
                    type: alertData.type
                });
            });

            // Limpiar al desmontar
            return () => {
                console.log('NotificationCenter: Eliminando listeners de socket');
                socket.off('new_notification');
                socket.off('unread_notifications');
                socket.off('important_alert');
            };
        } catch (socketError) {
            console.error('Error al configurar eventos de socket:', socketError);
        }
    }, [socket, client, isConnected]);

    // Mostrar error de socket si existe
    useEffect(() => {
        if (error) {
            console.warn('Error de socket en NotificationCenter:', error);
            setAlert({
                severity: 'warning',
                title: 'Problema de conexión',
                message: 'Las notificaciones en tiempo real podrían no estar disponibles'
            });
        }
    }, [error]);

    const fetchNotifications = async () => {
        try {
            const response = await fetch('/api/client/notifications', {
                headers: {
                    'Authorization': `Bearer ${clientToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setNotifications(data);
                setUnreadCount(data.filter(n => !n.read).length);
            }
        } catch (error) {
            console.error('Error al cargar notificaciones:', error);
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMarkAsRead = async (id) => {
        try {
            const response = await fetch(`/api/client/notifications/${id}/read`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${clientToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Actualizar estado local
                setNotifications(prev =>
                    prev.map(notification =>
                        notification.id === id ? { ...notification, read: true } : notification
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error al marcar notificación como leída:', error);
        }
    };

    const handleNotificationClick = async (notification) => {
        if (notification.id && !notification.read) {
            await handleMarkAsRead(notification.id);
        }
        
        handleMenuClose();

        const type = notification.type;
        const title = notification.title || '';
        if (type === 'invoice' || type === 'invoice_created') {
            navigate('/client/invoices');
        } else if (type === 'payment' || type === 'payment_due' || type === 'payment_upcoming' || type === 'service_cut') {
            navigate('/client/payments');
        } else if (type === 'support') {
            navigate('/client/support');
        } else if (type === 'promotion' || type === 'promotion_created' || title.toLowerCase().includes('oferta') || title.toLowerCase().includes('promoción') || title.toLowerCase().includes('promo')) {
            navigate('/client/promotions');
        } else {
            navigate('/client/dashboard');
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            const response = await fetch(`/api/client/notifications/read-all`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${clientToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Actualizar estado local
                setNotifications(prev =>
                    prev.map(notification => ({ ...notification, read: true }))
                );
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error al marcar todas las notificaciones como leídas:', error);
        }
    };

    const handleAlertClose = () => {
        setAlert(null);
    };

    // Función para obtener el icono según el tipo de notificación
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'payment':
            case 'payment_due':
            case 'payment_upcoming':
                return <PaymentIcon />;
            case 'invoice':
            case 'invoice_created':
                return <ReceiptIcon />;
            case 'support':
                return <SupportIcon />;
            case 'service_cut':
                return <WarningIcon color="error" />;
            default:
                return <InfoIcon />;
        }
    };

    // Función para obtener la severidad de alerta según el tipo
    const getNotificationSeverity = (type) => {
        switch (type) {
            case 'service_cut':
                return 'error';
            case 'payment_due':
                return 'warning';
            case 'payment_upcoming':
                return 'info';
            case 'invoice_created':
                return 'info';
            default:
                return 'info';
        }
    };

    // Función para formatear fecha relativa
    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 60) {
            return `hace ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
        } else {
            const diffHours = Math.floor(diffMs / 3600000);
            if (diffHours < 24) {
                return `hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
            } else {
                const diffDays = Math.floor(diffMs / 86400000);
                if (diffDays < 7) {
                    return `hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
                } else {
                    return date.toLocaleDateString();
                }
            }
        }
    };

    const open = Boolean(anchorEl);

    return (
        <>
            {/* Botón de notificaciones */}
            <Tooltip title="Notificaciones">
                <IconButton
                    color="inherit"
                    onClick={handleMenuOpen}
                    aria-controls={open ? 'notification-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Badge badgeContent={unreadCount} color="error">
                        {unreadCount > 0 ? (
                            <NotificationsActiveIcon color="primary" />
                        ) : (
                            <NotificationsIcon />
                        )}
                    </Badge>
                </IconButton>
            </Tooltip>

            {/* Menú de notificaciones */}
            <Menu
                id="notification-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        maxHeight: 500,
                        width: 360,
                        overflowX: 'hidden'
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Notificaciones</Typography>
                    {unreadCount > 0 && (
                        <Button
                            size="small"
                            startIcon={<MarkReadIcon />}
                            onClick={handleMarkAllAsRead}
                        >
                            Marcar todas como leídas
                        </Button>
                    )}
                </Box>

                <Divider />

                {notifications.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            No tienes notificaciones
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ width: '100%', p: 0 }}>
                        {notifications.map(notification => (
                            <React.Fragment key={notification.id}>
                                <ListItem
                                    alignItems="flex-start"
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: notification.read ? 'transparent' : 'action.hover',
                                        borderLeft: notification.read ? 'none' : '4px solid',
                                        borderColor: theme => {
                                            const severity = getNotificationSeverity(notification.type);
                                            return severity === 'error' ? theme.palette.error.main :
                                                severity === 'warning' ? theme.palette.warning.main :
                                                    theme.palette.primary.main;
                                        },
                                        transition: 'background-color 0.2s',
                                        '&:hover': {
                                            bgcolor: notification.read ? 'action.hover' : 'action.selected'
                                        }
                                    }}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{
                                            bgcolor: theme => {
                                                const severity = getNotificationSeverity(notification.type);
                                                return severity === 'error' ? theme.palette.error.light :
                                                    severity === 'warning' ? theme.palette.warning.light :
                                                        theme.palette.primary.light;
                                            },
                                            color: theme => {
                                                const severity = getNotificationSeverity(notification.type);
                                                return severity === 'error' ? theme.palette.error.dark :
                                                    severity === 'warning' ? theme.palette.warning.dark :
                                                        theme.palette.primary.dark;
                                            }
                                        }}>
                                            {getNotificationIcon(notification.type)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}
                                            >
                                                {notification.title}
                                            </Typography>
                                        }
                                        secondary={
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                    sx={{
                                                        display: 'block',
                                                        mb: 0.5,
                                                        fontSize: '0.875rem',
                                                        fontWeight: notification.read ? 'normal' : 'medium'
                                                    }}
                                                >
                                                    {notification.message}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {formatRelativeTime(notification.created_at)}
                                                </Typography>
                                            </>
                                        }
                                    />
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                    </List>
                )}

                <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Button
                        size="small"
                        onClick={() => {
                            handleMenuClose();
                            navigate('/client/notifications');
                        }}
                    >
                        Ver todas las notificaciones
                    </Button>
                </Box>
            </Menu>

            {/* Alerta para notificaciones importantes */}
            <Snackbar
                open={!!alert}
                autoHideDuration={10000}
                onClose={handleAlertClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {alert && (
                    <Alert
                        onClose={handleAlertClose}
                        severity={alert.severity}
                        variant="filled"
                        sx={{ 
                            width: '100%', 
                            cursor: alert.type ? 'pointer' : 'default',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: alert.type ? 'scale(1.015)' : 'none'
                            }
                        }}
                        onClick={(e) => {
                            // Prevenir redirección al dar clic en el botón de cerrar
                            if (e.target.closest('.MuiAlert-action') || e.target.closest('button')) {
                                return;
                            }
                            if (!alert.type && !alert.title) return;
                            
                            const type = alert.type;
                            const title = alert.title || '';
                            let url = '/client/dashboard';
                            if (type === 'invoice' || type === 'invoice_created') {
                                url = '/client/invoices';
                            } else if (type === 'payment' || type === 'payment_due' || type === 'payment_upcoming' || type === 'service_cut') {
                                url = '/client/payments';
                            } else if (type === 'support') {
                                url = '/client/support';
                            } else if (type === 'promotion' || type === 'promotion_created' || title.toLowerCase().includes('oferta') || title.toLowerCase().includes('promoción') || title.toLowerCase().includes('promo')) {
                                url = '/client/promotions';
                            }
                            navigate(url);
                            handleAlertClose();
                        }}
                    >
                        <AlertTitle>{alert.title}</AlertTitle>
                        {alert.message}
                    </Alert>
                )}
            </Snackbar>
        </>
    );
};

export default NotificationCenter;